import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { isPointingManagementType, parseDomain } from '@wix/domains-serverless-interface';
import { DomainSearchError } from '../types';
import { fetchWithFallback } from '@wix/premium-fed-sdk/@apis-infra';
import { getError, getErrorMessage, hasActionError, overrideCapabilityConfigs, shouldThrowConditionalError } from './utils';
import { LOGGER_DOMAINS_SEARCH_SUITE_ENTITY } from '../../../constants';
import { ACTION_CONFIG, capabilityConfigs, getErrorStatus } from './actionConfig';
import { extractResult } from '../utils';
/**
 * Status resolver that determines what actions are available for a domain
 * Based on the domains-serverless-interface aggregations
 */
export class DomainStatusResolver {
  constructor(httpClient, context) {
    _defineProperty(this, "httpClient", void 0);
    _defineProperty(this, "context", void 0);
    _defineProperty(this, "actionConfig", void 0);
    this.httpClient = httpClient;
    this.context = context;
    this.actionConfig = ACTION_CONFIG({
      isSiteLevel: Boolean(context.msid)
    });
  }

  /**
   * Resolve domain status and available actions
   */
  async resolveStatus(_ref) {
    let domainName = _ref.domainName,
      overrides = _ref.overrides;
    const _this$context = this.context,
      msid = _this$context.msid,
      externalViewer = _this$context.externalViewer;
    if (!domainName) {
      return {
        status: undefined
      };
    }
    const parsedDomain = parseDomain(domainName);
    if (parsedDomain.error) {
      return {
        status: parsedDomain.error
      };
    }
    try {
      const _await$this$_getCapab = await this._getCapabilites({
          domainName,
          overrides
        }),
        configs = _await$this$_getCapab.configs,
        account = _await$this$_getCapab.account;
      const status = this.determineActionWithErrorHandling(configs, parsedDomain);
      const errorStatus = getErrorStatus({
        configs,
        account,
        domain: domainName,
        externalViewer,
        status
      });
      if (errorStatus) {
        return {
          status: errorStatus
        };
      }

      // A submitted domain that resolved to no action and no error surfaces as
      // the first-class NO_STATUS (default renders nothing; consumers override
      // it like any status). Empty input / technical errors stay undefined.
      return {
        status: status ?? DomainSearchError.NO_STATUS
      };
    } catch (error) {
      DomainsApiClient.query.domainsLogger({
        httpClient: this.httpClient,
        request: {
          error: error,
          metadata: {
            domain: domainName,
            msid
          },
          type: 'error',
          entity: LOGGER_DOMAINS_SEARCH_SUITE_ENTITY
        }
      });
      return {
        status: undefined,
        errors: {
          domain: 'Error checking domain status'
        }
      };
    }
  }
  determineActionWithErrorHandling(configs, parsedDomain) {
    // Get actions sorted by priority from ACTION_CONFIG
    const sortedActions = Object.keys(configs).filter(action => {
      var _configs$action, _this$actionConfig;
      return ((_configs$action = configs[action]) == null ? void 0 : _configs$action.capability) && ((_this$actionConfig = this.actionConfig) == null || (_this$actionConfig = _this$actionConfig[action]) == null || _this$actionConfig.businessConditions == null ? void 0 : _this$actionConfig.businessConditions(parsedDomain));
    }).sort((a, b) => this.actionConfig[a].priority - this.actionConfig[b].priority);

    // Find the first available action and handle errors according to rules
    for (const action of sortedActions) {
      if (configs[action]) {
        const actionConfig = configs[action];
        const hasError = (actionConfig == null ? void 0 : actionConfig.promiseResult) && hasActionError(actionConfig.promiseResult);
        if (hasError) {
          const shouldThrowError = this.shouldThrowError(action, configs, this.actionConfig);
          if (shouldThrowError) {
            const errorMessage = getErrorMessage(actionConfig.promiseResult);
            DomainsApiClient.query.domainsLogger({
              httpClient: this.httpClient,
              request: {
                error: getError(actionConfig.promiseResult),
                type: 'error',
                entity: LOGGER_DOMAINS_SEARCH_SUITE_ENTITY
              }
            });
            throw new Error(`${action} failed: ${errorMessage}`);
          }
        }
        return action;
      }
    }
    return undefined;
  }
  async _getCapabilites(_ref2) {
    var _extractResult, _extractResult2, _extractResult3;
    let domainName = _ref2.domainName,
      overrides = _ref2.overrides;
    const _parseDomain = parseDomain(domainName),
      asciiDomain = _parseDomain.asciiDomain;
    const _DomainsApiClient$que = DomainsApiClient.queries({
        httpClient: this.httpClient
      }),
      canConnectOrBindDomainSuite = _DomainsApiClient$que.canConnectOrBindDomainSuite,
      canPurchaseDomainSuite = _DomainsApiClient$que.canPurchaseDomainSuite,
      canTransferInDomain = _DomainsApiClient$que.canTransferInDomain,
      canChangeDomainOwner = _DomainsApiClient$que.canChangeDomainOwner,
      getIsOnSale = _DomainsApiClient$que.getIsOnSale,
      domainTlds = _DomainsApiClient$que.domainTlds,
      canAssignDomain = _DomainsApiClient$que.canAssignDomain,
      domainsAccountPermissions = _DomainsApiClient$que.domainsAccountPermissions,
      userDomain = _DomainsApiClient$que.userDomain;
    const actionsResultsReq = !overrides ? [canConnectOrBindDomainSuite({
      domain: domainName,
      msid: this.context.msid ?? ''
    }), canPurchaseDomainSuite({
      domain: domainName
    }), canTransferInDomain({
      domain: domainName
    }), fetchWithFallback(canChangeDomainOwner({
      domain: asciiDomain
    }), {
      canChangeOwner: false
    }), fetchWithFallback(domainTlds({}, {
      staleTime: Infinity,
      cacheTime: Infinity
    }), {
      tlds: []
    }), fetchWithFallback(getIsOnSale({}), {
      isOnSale: false
    })] : [];
    const _this$context2 = this.context,
      msid = _this$context2.msid,
      externalViewer = _this$context2.externalViewer,
      hasZeroSites = _this$context2.hasZeroSites,
      msidIsSelectedSite = _this$context2.msidIsSelectedSite;
    const ownedApexManagementType = externalViewer ? fetchWithFallback(userDomain({
      domain: asciiDomain
    }), {}).then(_ref3 => {
      let domain = _ref3.domain;
      return domain == null ? void 0 : domain.managementType;
    }) : Promise.resolve(undefined);

    // accountParams scoped its premium check to the msid the server resolved
    // when the caller supplied none, so the same msid has to reach getSite.
    const permissions = fetchWithFallback(domainsAccountPermissions({}), {
      domainPermissions: []
    });
    const canAssign = permissions.then(_ref4 => {
      let resolvedMsid = _ref4.msid;
      return canAssignDomain({
        msid: msid ?? resolvedMsid
      });
    });
    const _await$Promise$allSet = await Promise.allSettled([permissions, canAssign, ownedApexManagementType, ...actionsResultsReq]),
      permissionsResult = _await$Promise$allSet[0],
      canAssignDomainResult = _await$Promise$allSet[1],
      ownedApexResult = _await$Promise$allSet[2],
      actionsResults = _await$Promise$allSet.slice(3);
    const connectOrBindResult = actionsResults[0],
      purchaseResult = actionsResults[1],
      transferResult = actionsResults[2];
    const overrideConfigs = overrideCapabilityConfigs(overrides);
    // Determine available actions based on capabilities and handle errors
    const configs = capabilityConfigs({
      ...overrideConfigs,
      actionConfigs: this.actionConfig,
      canAssignDomainResult,
      ...(connectOrBindResult && {
        connectOrBindResult
      }),
      ...(purchaseResult && {
        purchaseResult
      }),
      ...(transferResult && {
        transferResult
      })
    });
    const account = {
      canAssignDomain: Boolean((_extractResult = extractResult(canAssignDomainResult)) == null ? void 0 : _extractResult.canAssignDomain),
      domainPermissions: ((_extractResult2 = extractResult(permissionsResult)) == null ? void 0 : _extractResult2.domainPermissions) ?? [],
      msid: (msidIsSelectedSite ? msid : undefined) ?? ((_extractResult3 = extractResult(permissionsResult)) == null ? void 0 : _extractResult3.msid) ?? undefined,
      hasZeroSites: Boolean(hasZeroSites),
      ownedApexIsPointing: ownedApexResult.status === 'fulfilled' && isPointingManagementType(ownedApexResult.value)
    };
    return {
      configs,
      account
    };
  }

  /**
   * Determine if we should throw an error for a given action based on the mapping rules
   */
  shouldThrowError(action, configs, actionConfig) {
    const config = actionConfig[action];
    switch (config == null ? void 0 : config.errorEnforceRule) {
      case 'always':
        return true;
      case 'never':
        return false;
      case 'conditional':
        // For conditional rules, check if all dependency actions didn't fail
        return shouldThrowConditionalError(config.dependencies, configs);
      default:
        return false;
    }
  }
}
//# sourceMappingURL=statusResolver.js.map